// frontend/src/utils/inventoryTargetMode.js
//
// `inventory_target_mode` and `target_days_of_supply` — how the PO engine
// sizes the buffer it orders up to, and the days-of-supply target the second
// mode reads. Wire values and bounds mirror `core.models.InventoryTargetMode`
// and `TARGET_DAYS_OF_SUPPLY_MIN/MAX` (backend/core/models.py), which
// are the authority; the same setting cascades Store → Supplier →
// Item-location, so every screen that offers it imports from here.
//
//   SAFETY_STOCK    recommendations keep a calculated safety stock.
//   DAYS_OF_SUPPLY  recommendations top stock up to N days of demand after
//                   the order arrives.
//
// Under either, the buffer is never below the pair's Minimum stock.

export const INVENTORY_TARGET_MODE = Object.freeze({
  SAFETY_STOCK: 'SAFETY_STOCK',
  DAYS_OF_SUPPLY: 'DAYS_OF_SUPPLY',
});

// The order every picker renders the two modes in. Days of supply leads
// because it is the mode whose number sits beside the picker: a merchant
// choosing it sees the field it drives without moving.
export const INVENTORY_TARGET_MODES = Object.freeze([
  INVENTORY_TARGET_MODE.DAYS_OF_SUPPLY,
  INVENTORY_TARGET_MODE.SAFETY_STOCK,
]);

/**
 * Whether an (effective) mode READS the days-of-supply target. Under Safety
 * stock the target is still stored — it is what a flip to Days of supply will
 * use — but every screen mutes, annotates or hides it as unused, and this is
 * the one predicate they share.
 */
export const readsDaysOfSupply = (mode) => mode === INVENTORY_TARGET_MODE.DAYS_OF_SUPPLY;

/**
 * Whether an (effective) mode READS the safety stock and the service level
 * that produced it. The mirror of `readsDaysOfSupply`, and the reason both
 * exist: each mode leaves the OTHER mode's fields inert, and a screen that
 * mutes only one direction tells half the truth. Under Days of supply the
 * engine reads neither the pair's safety stock nor its service level — the
 * days the merchant chose, floored at the minimum stock, are the whole buffer
 * (backend/purchase_orders_engine/inventory_target.py) — so a live, editable
 * Safety stock cell on such a row invites a decision that changes nothing.
 *
 * Both fields stay STORED and editable under either mode: they are what a
 * flip back to Safety stock will use. Muting is a statement about what the
 * plan currently reads, never a lock.
 */
export const readsSafetyStock = (mode) => mode === INVENTORY_TARGET_MODE.SAFETY_STOCK;

export const TARGET_DAYS_OF_SUPPLY_MIN = 1;
export const TARGET_DAYS_OF_SUPPLY_MAX = 365;

/**
 * Whether a field's string is a days-of-supply target the backend will
 * accept: a whole number inside the bounds. Same predicate as
 * `core.models.target_days_of_supply_is_valid`, over the string a form
 * control holds rather than a parsed JSON value.
 */
export function isValidTargetDaysOfSupply(text) {
  if (!/^\d+$/.test(text)) return false;
  const days = Number(text);
  return days >= TARGET_DAYS_OF_SUPPLY_MIN && days <= TARGET_DAYS_OF_SUPPLY_MAX;
}
